import React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import theme from "../components/Theme";
import { usePlaceDetails } from "../hooks/usePlaceDetails";
import { useUserFavorites } from "../hooks/useUserFavorites";
import { PlaceHeader } from "../components/PlaceHeader";
import { PlaceImages } from "../components/PlaceImages";
import { PlaceMap } from "../components/PlaceMap";
import { PlaceInfo } from "../components/PlaceInfo";
import { PlaceContacts } from "../components/PlaceContacts";
import ReservationDialog from "../components/ReservationDialog";
import AddCommentDialog from "../components/AddCommentDialog";
import CommentList from "../components/CommentListComponent";

/**
 * ViewPlace container component displaying place details.
 * Refactored from 476 lines to <150 lines using Container/Presentational pattern.
 */
const ViewPlace = () => {
  const { id } = useParams();
  const { place, isLoading, error, refetch } = usePlaceDetails(id);
  const { isAuthenticated, userId, authToken } = useAuth();
  
  // Fetch favorites once on route load
  const { data: favorites = [], refetch: refetchFavorites } = useUserFavorites(userId);

  // Loading state
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Error state
  if (error || !place) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error loading place
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error?.message || 'Place not found'}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <Grid container sx={{ pt: 5, px: { xs: 2, md: 5, lg: 20 } }}>
          {/* Images and Map - on top */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8} lg={8}>
              <PlaceImages images={place.images || []} />
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <PlaceMap place={place} height="40vh" />
            </Grid>
          </Grid>

          {/* Header with name, address, rating, favorites - below images/map */}
          <Grid item xs={12}>
            <PlaceHeader
              place={place}
              isAuthenticated={isAuthenticated}
              userId={userId}
              authToken={authToken}
              onRefetch={() => {
                refetch();
                refetchFavorites();
              }}
              favorites={favorites}
            />
          </Grid>

          {/* Place Info and Contacts/Reservation */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* About this place - left side */}
            <Grid item xs={12} md={7} lg={9}>
              <PlaceInfo place={place} />
            </Grid>
            
            {/* Contacts and Reservation - right side in Paper */}
            <Grid item xs={12} md={5} lg={3}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                      Contacts
                    </Typography>

                {(place as any).contacts && (
                  <>

                    <PlaceContacts contacts={(place as any).contacts} />
                  </>
                )}

              </Paper>
              <Box sx={{ mb: 3 }}>
                  <ReservationDialog
                    placeId={place._id}
                    userId={userId}
                    authToken={authToken}
                    isAuthenticated={isAuthenticated}
                    refetchPlaceData={() => refetch()}
                  />
                </Box>
            </Grid>
          </Grid>

          {/* Comments Section */}
          <Grid container sx={{ mt: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Comments {place.comments && place.comments.length > 0 ? `(${place.comments.length})` : ''}
              </Typography>
              <AddCommentDialog
                placeId={place._id}
                userId={userId}
                authToken={authToken}
                isAuthenticated={isAuthenticated}
                refetchPlaceData={() => refetch()}
              />
              <CommentList
                comments={place.comments || []}
                placeId={place._id}
                isAuthenticated={isAuthenticated}
                userId={userId}
                authToken={authToken}
                refetchPlaceData={() => refetch()}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default ViewPlace;
